<template>
  <v-timeline-item fill-dot v-bind="$attrs">
    <!-- event icon -->
    <template #icon>
      <v-icon small color="white">
        $freshvuehistory-event-{{ item.event }}
      </v-icon>
    </template>

    <!-- timestamps / userstamps -->
    <template #opposite>
      <!-- created_by -->
      <div>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon x-small class="mr-2" v-on="on">
              $freshvuehistory-created-by
            </v-icon>
          </template>
          {{ $t('fresh-vue-history.layout.timeline.headers.created_by') }}
        </v-tooltip>
        <span>{{ item.created_by.name }}</span>
      </div>

      <!-- created_at -->
      <div>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon x-small class="mr-2" v-on="on">
              $freshvuehistory-created-at
            </v-icon>
          </template>
          {{ $t('fresh-vue-history.layout.timeline.headers.created_at') }}
        </v-tooltip>
        <span>{{ item.created_at | dateAndTime }}</span>
      </div>

      <!-- duration -->
      <div v-if="showDuration && item.duration">
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon x-small color="accent" class="mr-2" v-on="on">
              $freshvuehistory-duration
            </v-icon>
          </template>
          {{ $t('fresh-vue-history.layout.timeline.headers.duration-tooltip') }}
        </v-tooltip>
        <span class="accent--text">{{ item.duration | humanizeDuration }}</span>
      </div>
    </template>

    <div v-if="$vuetify.breakpoint.mobile" class="mb-2">
      <!-- created_by -->
      <div>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon x-small class="mr-2" v-on="on">
              $freshvuehistory-created-by
            </v-icon>
          </template>
          {{ $t('fresh-vue-history.layout.timeline.headers.created_by') }}
        </v-tooltip>
        <span>{{ item.created_by.name }}</span>
      </div>

      <!-- created_at -->
      <div>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon x-small class="mr-2" v-on="on">
              $freshvuehistory-created-at
            </v-icon>
          </template>
          {{ $t('fresh-vue-history.layout.timeline.headers.created_at') }}
        </v-tooltip>
        <span>{{ item.created_at | dateAndTime }}</span>
      </div>

      <!-- duration -->
      <div v-if="showDuration && item.duration">
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon x-small color="accent" class="mr-2" v-on="on">
              $freshvuehistory-duration
            </v-icon>
          </template>
          {{ $t('fresh-vue-history.layout.timeline.headers.duration-tooltip') }}
        </v-tooltip>
        <span class="accent--text">{{ item.duration | humanizeDuration }}</span>
      </div>
    </div>

    <!-- details -->
    <card-base dense :title="title" :no-padding="$vuetify.breakpoint.mobile">
      <template #toolbarIconActions>
        <v-dialog
          v-if="item.audit && item.audit.event === 'updated'"
          max-width="800"
        >
          <template #activator="{ on: dialog }">
            <v-tooltip left>
              <template #activator="{ on: tooltip }">
                <v-btn small icon class="ma-0" v-on="{ ...dialog, ...tooltip }">
                  <v-icon small>$freshvuehistory-show-history</v-icon>
                </v-btn>
              </template>
              <span>{{
                $t('fresh-vue-history.layout.timeline.actions.show-change')
              }}</span>
            </v-tooltip>
          </template>

          <card-base>
            <slot name="audit-changes" :audit="item.audit">
              <audit-changes :audit="item.audit" />
            </slot>
          </card-base>
        </v-dialog>
      </template>

      <!-- details slot -->
      <slot name="details-prepend" :item="item" view="TIMELINE">
        <json-details v-if="item.data" dense :json="item.data" />
      </slot>

      <!-- description -->
      <v-list-item v-if="item.description">
        <v-list-item-action>
          <v-icon>$freshvuehistory-description</v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('fresh-vue-history.layout.timeline.headers.description') }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ item.description }}
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-dialog max-width="800">
            <template #activator="{ on: dialog }">
              <v-tooltip left>
                <template #activator="{ on: tooltip }">
                  <v-btn icon class="ma-0" v-on="{ ...dialog, ...tooltip }">
                    <v-icon small>$freshvuehistory-action-show</v-icon>
                  </v-btn>
                </template>
                <span>{{
                  $t(
                    'fresh-vue-history.layout.timeline.actions.show-full-description'
                  )
                }}</span>
              </v-tooltip>
            </template>

            <card-base>
              <v-textarea
                :value="item.description"
                label="Beschreibung"
                placeholder="Keine Beschreibung vorhanden."
                outlined
                hide-details
                no-resize
                readonly
                class="mt-4"
                rows="12"
                row-height="25"
              />
            </card-base>
          </v-dialog>
        </v-list-item-action>
      </v-list-item>

      <!-- note -->
      <v-list-item
        v-if="
          ['NOTE_CREATED', 'NOTE_DELETED'].includes(item.event) && item.note
        "
      >
        <v-list-item-action>
          <note-severity-icon :severity="item.note.severity" show-tooltip />
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title v-if="item.note.title">
            {{ item.note.title }}
          </v-list-item-title>
          <v-list-item-subtitle class="text-wrap">
            {{ item.note.description }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-list-item v-else-if="item.event === 'NOTE_CREATED'">
        <v-list-item-action>
          <v-icon small>$freshvuehistory-event-NOTE_DELETED</v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('fresh-vue-history.misc.no-note') }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <!-- details slot -->
      <slot name="details-append" :item="item" view="TIMELINE" />
    </card-base>
  </v-timeline-item>
</template>

<script>
import { CardBase } from '@freshsolutions/fresh-vue-cards'
import { JsonDetails } from '@freshsolutions/fresh-vue'
import { AuditChanges } from '@freshsolutions/fresh-vue-audits'
import { NoteSeverityIcon } from '@freshsolutions/fresh-vue-notes'

export default {
  name: 'HistoryViewTimelineItem',

  components: {
    CardBase,
    JsonDetails,
    AuditChanges,
    NoteSeverityIcon
  },

  props: {
    item: {
      type: Object,
      required: true
    },

    showDuration: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  computed: {
    title() {
      if (this.$freshVueHistory.eventTextFormatter) {
        return (
          this.$freshVueHistory.eventTextFormatter(this.item) ?? this.item.event
        )
      }
      return this.item.event
    }
  }
}
</script>
